import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, ImagePlus, Loader2, ShoppingCart, TreePine, MapPin, DollarSign, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { analyzeYard, addShoppingItem } from "@/lib/api";

export default function YardPlanPage({ deviceId }) {
  const navigate = useNavigate();
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImagePreview(ev.target.result);
      setImageBase64(ev.target.result.split(",")[1]);
      setPlan(null);
    };
    reader.readAsDataURL(file);
  };

  const handleAnalyze = async () => {
    if (!imageBase64) return;
    setLoading(true);
    try {
      const data = await analyzeYard(imageBase64, deviceId, notes);
      setPlan(data);
    } catch (err) {
      toast.error("Could not analyze the yard. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAllProducts = async (zone) => {
    try {
      const items = [...(zone.suggested_plants || []), ...(zone.products_needed || [])];
      for (const item of items) {
        await addShoppingItem({
          device_id: deviceId,
          name: item,
          category: zone.name || "yard-plan",
        });
      }
      toast.success(`Added ${items.length} items from "${zone.name}" to your shopping list`);
    } catch (err) {
      toast.error("Could not add items.");
    }
  };

  return (
    <div className="px-5 pt-6 pb-4" data-testid="yard-plan-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} data-testid="yard-back-btn" className="w-12 h-12 rounded-xl border-2 border-[#0A2918] flex items-center justify-center press-active hard-shadow-sm bg-white">
          <ArrowLeft size={24} strokeWidth={2.5} />
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2918]" style={{ fontFamily: 'Work Sans' }}>
          Yard Plan
        </h1>
      </div>

      {/* Upload Area */}
      {!imagePreview && (
        <div className="space-y-4">
          <div className="bg-[#F2F7F4] border-2 border-dashed border-[#0A2918] rounded-2xl p-8 flex flex-col items-center justify-center gap-4 min-h-[200px]">
            <TreePine size={48} strokeWidth={2} className="text-[#28593A]" />
            <p className="text-lg font-bold text-[#0A2918] text-center">Upload a photo of your yard</p>
            <p className="text-base text-[#2A4D3B] text-center">We'll create a personalized planting plan</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => cameraInputRef.current?.click()}
              data-testid="yard-take-photo-btn"
              className="h-14 bg-[#0A2918] text-white rounded-xl text-lg font-bold flex items-center justify-center gap-2 press-active hard-shadow"
            >
              <Camera size={22} strokeWidth={2.5} /> Take a Photo
            </button>
            <button
              onClick={() => libraryInputRef.current?.click()}
              data-testid="yard-choose-library-btn"
              className="h-14 bg-white text-[#0A2918] border-2 border-[#0A2918] rounded-xl text-lg font-bold flex items-center justify-center gap-2 press-active hard-shadow-sm"
            >
              <ImagePlus size={22} strokeWidth={2.5} /> From Library
            </button>
          </div>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleImageSelect}
            className="hidden"
            data-testid="yard-camera-input"
          />
          <input
            ref={libraryInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
            data-testid="yard-library-input"
          />
        </div>
      )}

      {/* Image Preview + Notes */}
      {imagePreview && !plan && (
        <div className="space-y-4">
          <div className="rounded-2xl overflow-hidden border-2 border-[#0A2918] hard-shadow">
            <img src={imagePreview} alt="Yard" className="w-full h-56 object-cover" data-testid="yard-preview" />
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any notes? (e.g., 'South-facing, clay soil, want low maintenance')"
            data-testid="yard-notes-input"
            className="w-full h-24 rounded-xl border-2 border-[#0A2918] px-4 py-3 text-lg font-medium bg-white text-[#0A2918] placeholder:text-[#2A4D3B]/50 focus:ring-4 focus:ring-[#FFA62B] focus:outline-none resize-none"
          />
          <div className="flex gap-3">
            <Button
              onClick={() => { setImagePreview(null); setImageBase64(null); setNotes(""); }}
              variant="outline"
              data-testid="yard-retake-btn"
              className="flex-1 h-14 text-lg font-bold border-2 border-[#0A2918] rounded-xl press-active hard-shadow-sm"
            >
              Retake
            </Button>
            <Button
              onClick={handleAnalyze}
              disabled={loading}
              data-testid="yard-analyze-btn"
              className="flex-1 h-14 text-lg font-bold bg-[#0A2918] text-white rounded-xl press-active hard-shadow"
            >
              {loading ? (
                <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={22} /> Analyzing...</span>
              ) : (
                <span className="flex items-center gap-2"><TreePine size={22} strokeWidth={2.5} /> Create Plan</span>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="mt-6 bg-[#F2F7F4] rounded-2xl p-8 border-2 border-[#0A2918] flex flex-col items-center gap-3 animate-gentle-pulse">
          <Loader2 className="animate-spin text-[#28593A]" size={40} />
          <p className="text-lg font-bold text-[#0A2918]">Creating your yard plan...</p>
          <p className="text-base text-[#2A4D3B]">Analyzing sun, soil, and space</p>
        </div>
      )}

      {/* Results */}
      {plan && (
        <div className="space-y-4 animate-slide-up" data-testid="yard-plan-result">
          {/* Yard Image */}
          {imagePreview && (
            <div className="rounded-2xl overflow-hidden border-2 border-[#0A2918] hard-shadow">
              <img src={imagePreview} alt="Yard" className="w-full h-40 object-cover" />
            </div>
          )}

          {/* Assessment */}
          {plan.assessment && (
            <div className="bg-white border-2 border-[#0A2918] rounded-2xl p-5 hard-shadow" data-testid="yard-assessment">
              <h2 className="text-xl font-bold text-[#0A2918] mb-2" style={{ fontFamily: 'Work Sans' }}>Assessment</h2>
              <p className="text-base text-[#2A4D3B] leading-relaxed">{plan.assessment}</p>
            </div>
          )}

          {/* Meta row */}
          <div className="grid grid-cols-2 gap-3">
            {plan.estimated_budget && (
              <div className="bg-[#F2F7F4] border-2 border-[#0A2918] rounded-xl p-3 flex items-center gap-2 hard-shadow-sm">
                <DollarSign size={22} strokeWidth={2.5} className="text-[#28593A]" />
                <div>
                  <span className="text-xs font-bold text-[#0A2918] uppercase">Budget</span>
                  <p className="text-sm text-[#2A4D3B]">{plan.estimated_budget}</p>
                </div>
              </div>
            )}
            {plan.seasonal_notes && (
              <div className="bg-[#F2F7F4] border-2 border-[#0A2918] rounded-xl p-3 flex items-center gap-2 hard-shadow-sm">
                <Calendar size={22} strokeWidth={2.5} className="text-[#28593A]" />
                <div>
                  <span className="text-xs font-bold text-[#0A2918] uppercase">Timing</span>
                  <p className="text-sm text-[#2A4D3B]">{plan.seasonal_notes}</p>
                </div>
              </div>
            )}
          </div>

          {/* Zones */}
          {plan.zones?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xl font-bold text-[#0A2918]" style={{ fontFamily: 'Work Sans' }}>Planting Zones</h3>
              {plan.zones.map((zone, i) => (
                <div key={i} className="bg-white border-2 border-[#0A2918] rounded-2xl p-5 hard-shadow" data-testid={`yard-zone-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin size={20} strokeWidth={2.5} className="text-[#E25F45]" />
                    <h4 className="text-lg font-bold text-[#0A2918]" style={{ fontFamily: 'Work Sans' }}>{zone.name}</h4>
                  </div>
                  <p className="text-base text-[#2A4D3B] mb-3 leading-relaxed">{zone.description}</p>
                  
                  {zone.suggested_plants?.length > 0 && (
                    <div className="mb-2">
                      <span className="text-sm font-bold text-[#0A2918] uppercase">Plants:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {zone.suggested_plants.map((p, j) => (
                          <span key={j} className="bg-[#F2F7F4] text-[#0A2918] text-sm font-semibold px-3 py-1 rounded-full border border-[#0A2918]/30">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {zone.products_needed?.length > 0 && (
                    <div className="mb-3">
                      <span className="text-sm font-bold text-[#0A2918] uppercase">Products Needed:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {zone.products_needed.map((p, j) => (
                          <span key={j} className="bg-[#E25F45]/10 text-[#E25F45] text-sm font-semibold px-3 py-1 rounded-full border border-[#E25F45]/30">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={() => handleAddAllProducts(zone)}
                    data-testid={`add-zone-products-${i}`}
                    className="w-full h-12 text-base font-bold bg-[#28593A] text-white rounded-xl press-active"
                  >
                    <ShoppingCart size={18} strokeWidth={2.5} className="mr-2" /> Add to Shopping List
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* General Tips */}
          {plan.general_tips?.length > 0 && (
            <div className="bg-[#E25F45] text-white rounded-2xl p-5 hard-shadow-accent" data-testid="yard-tips">
              <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Work Sans' }}>Tips for Your Yard</h3>
              <ul className="space-y-2">
                {plan.general_tips.map((tip, i) => (
                  <li key={i} className="text-base leading-relaxed flex gap-2">
                    <span className="text-white/80 font-bold">{i + 1}.</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* New Scan */}
          <Button
            onClick={() => { setImagePreview(null); setImageBase64(null); setPlan(null); setNotes(""); }}
            variant="outline"
            data-testid="yard-new-scan-btn"
            className="w-full h-14 text-lg font-bold border-2 border-[#0A2918] rounded-xl press-active hard-shadow-sm"
          >
            Analyze Another Yard
          </Button>
        </div>
      )}
    </div>
  );
}
